import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { navigationInteraction } from "@WhiteCap/Common/Utillities/CustomTracking";
// eslint-disable-next-line spire/fenced-imports
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: FooterLinkListStyles;
}

export interface FooterLinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    footerLink?: InjectableCss;
}

export const footerLinkListStyles: FooterLinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 33px 0 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 4px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 0px !important;
                    `,
                    css`
                        margin-bottom: 0px !important;
                    `,
                    css`
                        margin-bottom: 0px !important;
                    `,
                    null,
                    null,
                ])}
            span[class^="TypographyStyle"] {
                opacity: 1;
                text-align: left;
                font-weight: 700;
                font-size: 12px;
                line-height: 22px;
                color: ${getColor("primary.contrast")};
                cursor: pointer;
                font-style: normal;
                text-transform: uppercase;
                &:hover {
                    color: #fc3 !important;
                    text-decoration: none !important;
                }
            }
            span {
                &.list-title {
                    cursor: default;
                    span {
                        cursor: default;
                    }
                }
            }
        `,
    },
    title: {
        css: css`
            > span {
                opacity: 1;
                text-align: left;
                text-transform: uppercase;
                &:hover {
                    color: #fc3 !important;
                    text-decoration: none !important;
                }
            }
        `,
    },
    linkWrapper: {
        css: css`
            span[class^="TypographyStyle"] {
                text-transform: capitalize;
                color: ${getColor("primary.contrast")};
                font-weight: 400 !important;
                font-size: 14px !important;
            }
        `,
    },
    footerLink: {
        css: css`
            span[class^="TypographyStyle"] {
                font-style: normal;
                font-weight: 700;
                font-size: 14px;
                line-height: 22px;
                color: ${getColor("primary.contrast")};
            }
        `,
    },
};

const defaultCustomCss = `.list-wrapper {

}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const FooterLinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", footerLinkListStyles, extendedStyles);

    const footerLinkList = (title: string) => {
        navigationInteraction({
            event: "navigation_interaction",
            navigation_category: `Footer - ${title}`,
            navigation_click: title,
        });
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
    };
    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
            ${styles.linkListWrapper?.css}
        `,
    };

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal" ? "display: inline-block;" : "padding: 1px 0 0 0;"};
            ${styles.linkWrapper?.css}
            margin-left: 15px;
            margin-bottom: 6px;
        `,
    };

    const customCssWrapper = {
        css: css`
            display: flex;
            ${fields.customCSS};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 15px;
                        flex-direction: column;
                    `,
                    css`
                        margin-bottom: 15px;
                        flex-direction: column;
                    `,
                    css`
                        margin-bottom: 5px;
                        justify-content: flex-start;
                    `,
                    css``,
                    css``,
                ])}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                    {titleLink ? (
                        <Link className="list-title" href={titleLink.url} role="button">
                            {fields.title || titleLink.title}
                        </Link>
                    ) : (
                        <Typography className="list-title" as="h3" {...styles.title} role="button">
                            {fields.title}
                        </Typography>
                    )}
                </StyledWrapper>
               {links.links.map((link, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper className="links-wrapper" key={index} {...directionStyles}>
                        {link.url && link.title && (
                            <Link
                                className="list-link"
                                href={link.url}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                {...styles.footerLink}
                                onClick={() => {
                                    if (fields.links[index].fields.overriddenTitle || link.title) {
                                        const title = fields.links[index].fields.overriddenTitle
                                            ? fields.links[index].fields.overriddenTitle
                                            : (link.title as string);
                                        footerLinkList(title);
                                    }
                                }}
                            >
                                {fields.links[index].fields.overriddenTitle || link.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: FooterLinkList,
    definition: {
        group: "Footer",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;